import ChalkySticks from '@chalkysticks/sdk';
import State from './State';

/**
 * Mutations are a synchronous way of modifying state. They are generally
 * called through Actions. It's best to think of them as setters.
 *
 * @see https://vuex.vuejs.org/guide/mutations.html
 *
 * @class Mutations
 * @package Store/Module/Settings
 * @project ChalkySticks App
 */
const mutations = {
	/**
	 * Add a feed item to the feed structure
	 *
	 * @param State state
	 * @param IFeedStructure payload
	 * @return void
	 */
	addFeedItem(state: typeof State, payload: IFeedStructure): void {
		state.feedStructure.push(payload);
	},

	/**
	 * Remove a feed item by component name
	 *
	 * @param State state
	 * @param string componentName
	 * @return void
	 */
	removeFeedItem(state: typeof State, componentName: string): void {
		state.feedStructure = state.feedStructure.filter((item) => item.component !== componentName);
	},

	/**
	 * Set the API version from server config
	 *
	 * @param State state
	 * @param string payload
	 * @return void
	 */
	setApiVersion(state: typeof State, payload: string): void {
		ChalkySticks.Utility.Log.debug('store-settings', '🔮 Setting API version', payload);

		state.apiVersion = payload;
	},

	/**
	 * Set the app settings from server config
	 *
	 * @param State state
	 * @param Record<string, any> payload
	 * @return void
	 */
	setAppSettings(state: typeof State, payload: Record<string, any>): void {
		ChalkySticks.Utility.Log.debug('store-settings', '🔮 Setting app settings', payload);

		state.appSettings = payload;
	},

	/**
	 * Set the unique device identifier
	 *
	 * @param State state
	 * @param string value
	 * @return void
	 */
	setDeviceId(state: typeof State, value: string | null): void {
		state.deviceId = value;
	},

	/**
	 * Set the app features configuration
	 *
	 * @param State state
	 * @param IFeatures payload
	 * @return void
	 */
	setFeatures(state: typeof State, payload: IFeatures): void {
		ChalkySticks.Utility.Log.debug('store-settings', '🔮 Setting app features', payload);

		state.features = {
			...state.features,
			...payload,
		};
	},

	/**
	 * Set the feed structure configuration
	 *
	 * @param State state
	 * @param IFeedStructure[] payload
	 * @return void
	 */
	setFeedStructure(state: typeof State, payload: IFeedStructure[]): void {
		ChalkySticks.Utility.Log.debug('store-settings', '🔮 Setting app feed structure', payload);

		state.feedStructure = payload;
	},

	/**
	 * Set whether the user can use location services
	 *
	 * @param State state
	 * @param boolean value
	 * @return void
	 */
	setLocationDenied(state: typeof State, value: boolean): void {
		state.locationDenied = value;
	},

	/**
	 * Set the flag for onboarding completion
	 *
	 * @param State state
	 * @param boolean payload
	 * @return void
	 */
	setOnboardingComplete(state: typeof State, payload: boolean): void {
		state.onboardingComplete = payload;
	},

	/**
	 * Set the push notification token
	 *
	 * @param State state
	 * @param string value
	 * @return void
	 */
	setPushToken(state: typeof State, value: string): void {
		state.pushToken = value;
	},

	/**
	 * Set the PWA install status (accepted/dismissed)
	 *
	 * @param State state
	 * @param 'accepted' | 'dismissed' | null value
	 * @return void
	 */
	setPwaInstallStatus(state: typeof State, value: 'accepted' | 'dismissed' | null): void {
		state.pwaInstallStatus = value;
	},

	/**
	 * Set whether location services are enabled
	 *
	 * @param State state
	 * @param boolean value
	 * @return void
	 */
	setUseLocation(state: typeof State, value: boolean): void {
		state.useLocation = value;
	},

	/**
	 * Update the enabled status of a feed item
	 *
	 * @param State state
	 * @param Object payload
	 * @return void
	 */
	updateFeedItemStatus(state: typeof State, payload: { component: string; enabled: boolean }): void {
		const index = state.feedStructure.findIndex((item) => item.component === payload.component);
		if (index !== -1) {
			state.feedStructure[index].enabled = payload.enabled;
		}
	},
};

export default mutations;
